import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User, ChevronDown } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { Logo } from "./Logo";
import { useAuth } from "@/context/AuthContext";
import { SERVICES } from "@/data/services";

const NAV = [
  { label: "See Our Work", to: "/gallery" },
  { label: "Pricing", to: "/pricing" },
  { label: "Blog", to: "/blog" },
  { label: "Refer a Friend", to: "/refer" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menu, setMenu] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const [mobileSvc, setMobileSvc] = useState(false);
  const svcTimer = useRef(null);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const openSvc = () => { clearTimeout(svcTimer.current); setSvcOpen(true); };
  const closeSvc = () => { svcTimer.current = setTimeout(() => setSvcOpen(false), 120); };

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 transition-[background-color,box-shadow,border-color] duration-300 ${
        scrolled ? "bg-white/80 backdrop-blur-xl border-b border-brand-silver/40 shadow-[0_4px_30px_-20px_rgba(0,0,0,0.3)]" : "bg-white/40 backdrop-blur-md border-b border-transparent"
      }`}
    >
      <div className="container-rv flex items-center justify-between py-4">
        <Logo />

        <nav className="hidden items-center gap-9 lg:flex">
          <Link
            to="/gallery"
            data-testid="nav-gallery-link"
            className="relative text-sm font-medium text-brand-charcoal/80 transition-colors duration-200 hover:text-brand-red after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brand-red after:transition-all after:duration-300 hover:after:w-full"
          >
            See Our Work
          </Link>

          {/* Services dropdown */}
          <div className="relative" onMouseEnter={openSvc} onMouseLeave={closeSvc}>
            <Link
              to="/services"
              data-testid="nav-services-link"
              className="inline-flex items-center gap-1 text-sm font-medium text-brand-charcoal/80 transition-colors duration-200 hover:text-brand-red"
            >
              Services <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${svcOpen ? "rotate-180" : ""}`} />
            </Link>
            <AnimatePresence>
              {svcOpen && (
                <motion.div
                  data-testid="services-dropdown"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.18 }}
                  className="absolute left-1/2 top-full z-50 mt-3 w-72 -translate-x-1/2 overflow-hidden rounded-2xl border border-brand-silver/40 bg-white p-2 shadow-[0_30px_70px_-30px_rgba(26,28,30,0.45)]"
                >
                  {SERVICES.map((s) => (
                    <Link
                      key={s.slug}
                      to={`/services/${s.slug}`}
                      data-testid={`nav-service-${s.id}`}
                      className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-brand-offwhite"
                    >
                      <div className="h-11 w-11 shrink-0 overflow-hidden rounded-lg">
                        <img src={s.hero} alt={s.name} className="h-full w-full object-cover" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-brand-charcoal">{s.name}</p>
                        <p className="truncate text-xs text-brand-muted">{s.price}</p>
                      </div>
                    </Link>
                  ))}
                  <Link to="/services" className="mt-1 block rounded-xl px-3 py-2 text-center text-xs font-semibold text-brand-red hover:bg-brand-offwhite">
                    View all services →
                  </Link>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {NAV.filter((n) => n.to !== "/gallery").map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.to.replace("/", "")}-link`}
              className="relative text-sm font-medium text-brand-charcoal/80 transition-colors duration-200 hover:text-brand-red after:absolute after:-bottom-1.5 after:left-0 after:h-px after:w-0 after:bg-brand-red after:transition-all after:duration-300 hover:after:w-full"
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          {user ? (
            <div className="flex items-center gap-4">
              <Link
                to={user.role === "admin" ? "/admin" : "/dashboard"}
                data-testid="header-account-link"
                className="inline-flex items-center gap-2 text-sm font-medium text-brand-charcoal transition-colors hover:text-brand-red"
              >
                <User className="h-4 w-4 text-brand-red" /> {user.name?.split(" ")[0] || "Account"}
              </Link>
              <span className="h-4 w-px bg-brand-silver/50" />
              <button
                data-testid="header-logout-button"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-sm font-medium text-brand-muted transition-colors hover:text-brand-red"
              >
                Logout
              </button>
            </div>
          ) : (
            <Link
              to="/login"
              data-testid="header-login-link"
              className="text-sm font-medium text-brand-charcoal/80 transition-colors hover:text-brand-red"
            >
              Login
            </Link>
          )}
          <Link to="/book" data-testid="header-book-button" className="btn-red btn-pulse whitespace-nowrap">
            Schedule a Detailing Now
          </Link>
        </div>

        <button
          data-testid="mobile-menu-button"
          className="lg:hidden text-brand-charcoal"
          onClick={() => setMenu((m) => !m)}
          aria-label="Toggle menu"
        >
          {menu ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {menu && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
            className="overflow-hidden border-t border-brand-silver/40 bg-white lg:hidden"
          >
            <div className="container-rv flex flex-col gap-1 py-4">
              <Link to="/gallery" onClick={() => setMenu(false)}
                className="py-3 text-base font-medium text-brand-charcoal">See Our Work</Link>

              {/* Services (collapsible) */}
              <button
                data-testid="mobile-services-toggle"
                onClick={() => setMobileSvc((v) => !v)}
                className="flex items-center justify-between py-3 text-base font-medium text-brand-charcoal"
              >
                Services <ChevronDown className={`h-4 w-4 transition-transform ${mobileSvc ? "rotate-180" : ""}`} />
              </button>
              <AnimatePresence>
                {mobileSvc && (
                  <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden pl-3">
                    {SERVICES.map((s) => (
                      <Link key={s.slug} to={`/services/${s.slug}`} onClick={() => setMenu(false)}
                        data-testid={`mobile-service-${s.id}`}
                        className="block py-2.5 text-sm text-brand-charcoal/80">{s.name} · <span className="text-brand-muted">{s.price}</span></Link>
                    ))}
                    <Link to="/services" onClick={() => setMenu(false)} className="block py-2.5 text-sm font-semibold text-brand-red">All services →</Link>
                  </motion.div>
                )}
              </AnimatePresence>

              {NAV.filter((n) => n.to !== "/gallery").map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMenu(false)}
                  className="py-3 text-base font-medium text-brand-charcoal">{n.label}</Link>
              ))}
              <Link to={user ? "/" : "/login"} onClick={async () => { setMenu(false); if (user) await logout(); }}
                className="py-3 text-base font-medium text-brand-charcoal">{user ? "Logout" : "Login"}</Link>
              <Link to="/book" onClick={() => setMenu(false)} className="btn-red btn-pulse mt-2 w-full">Schedule a Detailing Now</Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
